import { useState } from "react"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { Close } from "@mui/icons-material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faMagnifyingGlass } from "@fortawesome/pro-light-svg-icons"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    FormHelperText,
    Grid,
    IconButton,
    Skeleton,
    Typography,
} from "@mui/material"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { CreateUpdateTemplateFormValues } from "../supplierPosSettingsTypes"
import { PosSettingQuery, PosThemesResponse, getPosThemes } from "../supplierPosSettingsApi"
import { useCurrentSupplier } from "../../../../../suppliers/supplierHooks"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"

const useStyles = makeStyles()((theme) => ({
    accordion: {
        height: "100%",
        width: "100%",
        boxShadow:
            "0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)",
    },
    accordionDetails: {
        height: "auto",
        width: "100%",
    },
    container: {
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: "1.25rem",
    },
    imageWrap: {
        borderRadius: "0.25rem",
        height: "6.25rem",
        width: "100%",
        position: "relative",
        cursor: "pointer",
        border: `2px solid ${theme.palette.tailwind.gray[50]}`,
    },
    imageWrapActive: {
        borderColor: theme.palette.common.black,
    },
    image: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
    },
    dialogContainer: {
        position: "relative",
        "& >img": {
            width: "100%",
            height: "auto",
            objectFit: "cover",
        },
    },
    buttonWrap: {
        color: "white",
        padding: "0.3rem",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.7)",
        },
    },
    glassIcon: {
        position: "absolute",
        right: "0.75rem",
        bottom: "0.75rem",
    },
    closeIcon: {
        position: "absolute",
        right: "1rem",
        top: "0.75rem",
    },
    hideEdge: { display: "none" },
}))
type FrontPageLayoutProps = Readonly<{}>

const selectThemes = (data: PosThemesResponse) => data?.themes || []
const defaultPreviewImageState = { show: false, image: "" }

export const FrontPageLayout = ({}: FrontPageLayoutProps) => {
    const { values, setFieldValue, errors } = useFormikContext<CreateUpdateTemplateFormValues>()
    const { supplierId } = useCurrentSupplier()
    const { classes, cx } = useStyles()

    const [isExpanded, setIsExpanded] = useState(true)
    const [previewImage, setPreviewImage] = useState<{ show: boolean; image: string }>(defaultPreviewImageState)

    const { data: themes = [], isLoading } = useApiQuery(getPosThemes, {
        queryName: PosSettingQuery.THEMES_LIST,
        dependencies: {
            supplierId: supplierId,
        },
        select: selectThemes,
    })

    return (
        <Grid container gap={2}>
            <LocalizedStrict id="supplier-admin-pos-settings-template-frontpage-layout">
                <Typography variant="subtitle1">Frontpage layout</Typography>
            </LocalizedStrict>
            <Accordion
                expanded={isExpanded}
                elevation={0}
                onChange={() => setIsExpanded(!isExpanded)}
                className={classes.accordion}
                id="layout.theme.id"
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <LocalizedStrict id="supplier-admin-pos-settings-template-select-theme-style">
                        <Typography variant="body2">Select theme style:</Typography>
                    </LocalizedStrict>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetails}>
                    <Box className={classes.container}>
                        {isLoading ? (
                            <>
                                <Skeleton variant="rectangular" height={100} />
                            </>
                        ) : (
                            themes.map((el) => (
                                <Box
                                    className={cx(classes.imageWrap, {
                                        [classes.imageWrapActive]: el.id === values.layout.theme.id,
                                    })}
                                    key={el.id}
                                    onClick={() => setFieldValue("layout.theme.id", el.id)}
                                >
                                    <img src={el.previewImage.url} alt="theme image" className={classes.image} />
                                    <IconButton
                                        edge="end"
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setPreviewImage({ show: true, image: el.previewImage.url })
                                        }}
                                        className={cx(classes.buttonWrap, classes.glassIcon)}
                                    >
                                        <FontAwesomeIcon icon={faMagnifyingGlass} size="xs" />
                                    </IconButton>
                                </Box>
                            ))
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>
            {errors?.layout?.theme?.id && (
                <FormHelperText error>
                    {Array.isArray(errors.layout?.theme?.id)
                        ? errors.layout?.theme?.id.join(", ")
                        : errors.layout?.theme?.id}
                </FormHelperText>
            )}

            {previewImage.show && (
                <StandardDialog
                    open={previewImage.show}
                    topbar={<></>}
                    onClose={() => setPreviewImage(defaultPreviewImageState)}
                    updateEdge={classes.hideEdge}
                    maxWidthProp="md"
                >
                    <Grid container className={classes.dialogContainer}>
                        <IconButton
                            edge="end"
                            size="small"
                            onClick={() => setPreviewImage(defaultPreviewImageState)}
                            className={cx(classes.buttonWrap, classes.closeIcon)}
                        >
                            <Close fontSize="inherit" />
                        </IconButton>
                        <img src={`${previewImage.image}`} alt="product image" />
                    </Grid>
                </StandardDialog>
            )}
        </Grid>
    )
}
